/**
 * Resources page search
 *
 * Previously this page used the predictive search endpoint, which only returns
 * up to ~10 items per type. That is why you only saw ~9 products. We now use
 * the Storefront GraphQL API to search products and paginate client-side,
 * filtering to products that actually have resources (metafield custom.sanity_files).
 */
(function () {
  const sections = document.querySelectorAll("[data-resources-page]");
  if (!sections.length) {
    return;
  }

  const rootUrl = (function () {
    var routes = window.Shopify && window.Shopify.routes;
    if (!routes || !routes.root) {
      return "";
    }
    return routes.root.endsWith("/") ? routes.root.slice(0, -1) : routes.root;
  })();

  sections.forEach(function (section) {
    if (!section) {
      return;
    }

    var form = section.querySelector("[data-resource-form]");
    var input = section.querySelector("[data-resource-input]");
    var resultsWrapper = section.querySelector("[data-resource-results]");
    var grid = section.querySelector("[data-resource-grid]");
    var feedback = section.querySelector("[data-resource-feedback]");
    var countsEl = section.querySelector("[data-resource-counts]");
    var paginationEl = section.querySelector("[data-resource-pagination]");

    if (!form || !input || !resultsWrapper || !grid || !feedback) {
      return;
    }

    // Normalize token to avoid attempting requests with placeholder defaults
    var rawToken = (section.getAttribute("data-storefront-token") || "").trim();
    var PLACEHOLDER_TOKENS = [
      "YOUR_STOREFRONT_PUBLIC_TOKEN",
      "REPLACE_WITH_STOREFRONT_TOKEN",
      "__REQUIRED__",
      "YOUR_STOREFRONT_TOKEN",
    ];
    var normalizedToken =
      PLACEHOLDER_TOKENS.indexOf(rawToken) !== -1 ? "" : rawToken;

    var config = {
      token: normalizedToken,
      apiVersion:
        section.getAttribute("data-storefront-api-version") || "2024-04",
      pageSize: parseInt(
        section.getAttribute("data-results-limit") || "32",
        10
      ),
      emptyMessage:
        section.getAttribute("data-empty-message") ||
        "No results found for your search.",
      errorMessage:
        section.getAttribute("data-error-message") ||
        "We could not load resources at this time.",
      loadingMessage:
        section.getAttribute("data-loading-message") || "Loading resources...",
    };

    var initialParams = new URLSearchParams(window.location.search);
    var initialQuery = initialParams.get("q");
    var initialPage = parseInt(initialParams.get("page") || "1", 10) || 1;

    if (!config.token || !config.apiVersion) {
      showFeedback(config.errorMessage, "error");
      return;
    }

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      var term = input.value.trim();
      updateQueryString({ q: term, page: 1 });

      if (!term) {
        clearResults();
        hideFeedback();
        return;
      }

      runSearch(term, 1);
    });

    if (initialQuery) {
      input.value = initialQuery;
      runSearch(initialQuery, initialPage);
    }

    function updateQueryString(params) {
      if (!window.history || !window.history.replaceState) {
        return;
      }
      var url = new URL(window.location.href);
      if (params.q !== undefined) {
        if (params.q) url.searchParams.set("q", params.q);
        else url.searchParams.delete("q");
      }
      if (params.page !== undefined) {
        if (params.page && params.page > 1)
          url.searchParams.set("page", String(params.page));
        else url.searchParams.delete("page");
      }
      window.history.replaceState({}, "", url.toString());
    }

    function hideFeedback() {
      feedback.textContent = "";
      feedback.classList.remove("is-error", "is-empty", "is-loading");
      feedback.hidden = true;
    }

    function showFeedback(message, state) {
      feedback.textContent = message;
      feedback.hidden = !message;
      feedback.classList.remove("is-error", "is-empty", "is-loading");
      if (state === "error") {
        feedback.classList.add("is-error");
      } else if (state === "empty") {
        feedback.classList.add("is-empty");
      } else if (state === "loading") {
        feedback.classList.add("is-loading");
      }
    }

    function clearResults() {
      grid.innerHTML = "";
      resultsWrapper.hidden = true;
      if (countsEl) {
        countsEl.innerHTML = "";
        countsEl.hidden = true;
      }
      if (paginationEl) {
        paginationEl.innerHTML = "";
        paginationEl.hidden = true;
      }
    }

    function sanitizeTerm(term) {
      var trimmed = term.trim();
      if (/^\d+$/.test(trimmed)) {
        return "JTD-" + trimmed;
      }
      return trimmed;
    }

    function productUrlFromHandle(handle) {
      if (!handle) return "";
      return rootUrl + "/products/" + handle;
    }

    function graphqlEndpoint() {
      return rootUrl + "/api/" + config.apiVersion + "/graphql.json";
    }

    function graphqlQueryAll(term, first) {
      return (
        "query searchProducts($query: String!, $first: Int!) {\n" +
        "  products(first: $first, query: $query) {\n" +
        "    edges {\n" +
        "      cursor\n" +
        "      node {\n" +
        "        title\n" +
        "        handle\n" +
        "        images(first: 1) { edges { node { url } } }\n" +
        '        metafield(namespace: "custom", key: "sanity_files") { value }\n' +
        "      }\n" +
        "    }\n" +
        "    pageInfo { hasNextPage hasPreviousPage }\n" +
        "  }\n" +
        "}"
      );
    }

    function fetchProductsWithResourcesAll(term) {
      var q = sanitizeTerm(term);
      var first = 250; // upper bound for Storefront API
      var body = JSON.stringify({
        query: graphqlQueryAll(q, first),
        variables: { query: q, first: first },
      });
      return fetch(graphqlEndpoint(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": config.token,
        },
        body: body,
      })
        .then(function (response) {
          if (!response.ok) throw new Error("GraphQL response not OK");
          return response.json();
        })
        .then(function (payload) {
          var edges =
            (payload &&
              payload.data &&
              payload.data.products &&
              payload.data.products.edges) ||
            [];
          var products = edges
            .map(function (edge) {
              var node = edge && edge.node;
              if (!node) return null;
              var imageUrl = "";
              try {
                var imgEdges = (node.images && node.images.edges) || [];
                imageUrl = imgEdges.length
                  ? imgEdges[0].node && imgEdges[0].node.url
                  : "";
              } catch (e) {}
              var resources = [];
              var raw = node.metafield && node.metafield.value;
              if (raw) {
                try {
                  var parsed = JSON.parse(raw);
                  if (Array.isArray(parsed)) {
                    resources = parsed
                      .map(function (item) {
                        return {
                          url: item.url || item.assetUrl || "",
                          filename:
                            item.filename ||
                            item.name ||
                            item.description ||
                            "",
                          description: item.description || "",
                          category: item.category || "",
                          version: item.version || "",
                        };
                      })
                      .filter(function (r) {
                        return !!r.url;
                      });
                  }
                } catch (e) {
                  // ignore JSON parse errors
                }
              }
              return {
                title: node.title || "",
                handle: node.handle || "",
                image: imageUrl || "",
                url: productUrlFromHandle(node.handle || ""),
                resources: resources,
              };
            })
            .filter(function (p) {
              return p && Array.isArray(p.resources) && p.resources.length;
            });
          console.log("products and edges", products, edges);
          return { products: products, totalFound: edges.length };
        });
    }

    function renderCards(products) {
      grid.innerHTML = "";
      if (!Array.isArray(products) || !products.length) {
        resultsWrapper.hidden = true;
        return;
      }

      var fragment = document.createDocumentFragment();

      products.forEach(function (product) {
        if (
          !product ||
          !Array.isArray(product.resources) ||
          !product.resources.length
        ) {
          return;
        }

        var card = document.createElement("div");
        card.className = "resource-card";

        // media
        var media = document.createElement("div");
        media.className = "resource-card__media";
        if (product.image) {
          var img = document.createElement("img");
          img.src = product.image;
          img.alt = product.title || "Product image";
          media.appendChild(img);
        }
        card.appendChild(media);

        // body
        var body = document.createElement("div");
        body.className = "resource-card__body";

        var titleEl = document.createElement("h3");
        titleEl.className = "resource-card__title";
        if (product.url) {
          var link = document.createElement("a");
          link.href = product.url;
          link.textContent = product.title || product.url;
          titleEl.appendChild(link);
        } else {
          titleEl.textContent = product.title || "";
        }
        body.appendChild(titleEl);

        var metaEl = document.createElement("div");
        metaEl.className = "resource-card__meta";
        metaEl.textContent = "Resources";
        body.appendChild(metaEl);

        var filesEl = document.createElement("div");
        filesEl.className = "resource-card__files";

        product.resources.forEach(function (res) {
          var file = document.createElement("div");
          file.className = "resource-card__file";

          var ft = document.createElement("div");
          ft.className = "resource-card__file-title";
          var name = res.filename || res.description || "Download";
          if (res.url) {
            var a = document.createElement("a");
            a.href = res.url;
            a.textContent = name;
            a.rel = "noopener";
            a.target = "_blank";
            ft.appendChild(a);
          } else {
            ft.textContent = name;
          }
          file.appendChild(ft);

          var fm = document.createElement("div");
          fm.className = "resource-card__file-meta";
          var parts = [];
          if (res.category) parts.push(res.category);
          if (res.version) parts.push("Version " + res.version);
          fm.textContent = parts.join(" • ");
          file.appendChild(fm);

          filesEl.appendChild(file);
        });

        body.appendChild(filesEl);
        card.appendChild(body);
        fragment.appendChild(card);
      });

      if (!fragment.childNodes.length) {
        resultsWrapper.hidden = true;
        return;
      }

      grid.appendChild(fragment);
      resultsWrapper.hidden = false;
    }

    function renderPagination(totalCount, pageSize, currentPage, onGoToPage) {
      if (!paginationEl) return;
      paginationEl.innerHTML = "";
      var totalPages = Math.ceil(totalCount / pageSize);
      if (totalPages <= 1) {
        paginationEl.hidden = true;
        return;
      }

      function createIconButton(disabled, targetPage, ariaLabel, svgPathD) {
        var li = document.createElement("li");
        li.className = "yv-pagination-item" + (disabled ? " disabled" : "");
        var tag = disabled ? "span" : "a";
        var el = document.createElement(tag);
        el.className = "yv-pagination-link yv-pagination-icon";
        el.setAttribute("aria-label", ariaLabel);
        if (!disabled) {
          el.href = buildPageUrl(targetPage);
          el.addEventListener("click", function (e) {
            e.preventDefault();
            onGoToPage(targetPage);
          });
        }
        el.innerHTML =
          '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" role="img" width="24px" height="24px" fill="none"><path stroke="currentColor" stroke-width="1.5" d="' +
          svgPathD +
          '"></path></svg>';
        li.appendChild(el);
        return li;
      }

      var ul = document.createElement("ul");
      ul.className = "list-unstyled yv-pagination";

      // Prev
      ul.appendChild(
        createIconButton(
          currentPage <= 1,
          currentPage - 1,
          "Previous",
          "M15.526 18.966L8.559 12l6.967-6.967"
        )
      );

      // Parts: show 1, ..., current-2..current+2, ..., last
      var parts = [];
      var range = 2; // neighbors around current
      var start = Math.max(1, currentPage - range);
      var end = Math.min(totalPages, currentPage + range);
      if (start > 1) parts.push(1);
      if (start > 2) parts.push("ellipsis");
      for (var i = start; i <= end; i++) parts.push(i);
      if (end < totalPages - 1) parts.push("ellipsis");
      if (end < totalPages) parts.push(totalPages);

      parts.forEach(function (part) {
        var li = document.createElement("li");
        if (part === "ellipsis") {
          li.className = "yv-pagination-item";
          li.textContent = "…";
        } else if (part === currentPage) {
          li.className = "yv-pagination-item yv-pagination-text active";
          li.textContent = String(part);
        } else {
          li.className = "yv-pagination-item";
          var a = document.createElement("a");
          a.href = buildPageUrl(part);
          a.className = "yv-pagination-link";
          a.setAttribute("aria-label", "Page " + part);
          a.textContent = String(part);
          a.addEventListener("click", function (e) {
            e.preventDefault();
            var p = parseInt(this.textContent, 10) || 1;
            onGoToPage(p);
          });
          li.appendChild(a);
        }
        ul.appendChild(li);
      });

      // Next
      ul.appendChild(
        createIconButton(
          currentPage >= totalPages,
          currentPage + 1,
          "Next",
          "M8.474 18.966L15.44 12 8.474 5.033"
        )
      );

      paginationEl.appendChild(ul);
      paginationEl.hidden = false;
    }

    function renderCounts(matched, filteredOut, total) {
      if (!countsEl) return;
      var html =
        "" +
        '<div class="count-item count-item--matched">Matches <strong>' +
        String(matched) +
        "</strong></div>" +
        '<div class="count-item">Filtered ' +
        String(filteredOut) +
        "</div>" +
        '<div class="count-item">Total ' +
        String(total) +
        "</div>";
      countsEl.innerHTML = html;
      countsEl.hidden = false;
      // Ensure results wrapper is visible to show counts even if grid is empty
      resultsWrapper.hidden = false;
    }

    function buildPageUrl(page) {
      var url = new URL(window.location.href);
      if (page && page > 1) url.searchParams.set("page", String(page));
      else url.searchParams.delete("page");
      return url.toString();
    }

    function buildMainSearchUrl(term) {
      var base = typeof searchUrl === "string" && searchUrl ? searchUrl : rootUrl + "/search";
      var u = new URL(base, window.location.origin);
      var raw = (term || "").trim();
      if (raw) u.searchParams.set("q", raw);
      u.searchParams.set("type", "product");
      u.searchParams.set("options[prefix]", "last");
      return u.toString();
    }

    function fetchMainSearchTotal(term) {
      var url = buildMainSearchUrl(term);
      return fetch(url, { method: "GET" })
        .then(function (r) {
          if (!r.ok) throw new Error("Search page not OK");
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var el = doc.querySelector(".yv-search-result-count") || doc.querySelector(".products-count");
          var text = el ? el.textContent || "" : "";
          var m = text.match(/(\d[\d,\.]*)/);
          var n = m ? parseInt(m[1].replace(/[^\d]/g, ""), 10) : 0;
          return isNaN(n) ? 0 : n;
        })
        .catch(function () {
          return 0;
        });
    }

    function runSearch(term, page) {
      showFeedback(config.loading_message || config.loadingMessage, "loading");
      clearResults();
      var currentPage = Math.max(1, parseInt(page || "1", 10) || 1);

      Promise.all([fetchMainSearchTotal(term), fetchProductsWithResourcesAll(term)])
        .then(function (arr) {
          var totalFound = arr && typeof arr[0] === "number" ? arr[0] : 0;
          var data = arr && arr[1] ? arr[1] : { products: [] };
          var results = Array.isArray(data.products) ? data.products : [];
          var matched = results.length;
          var filteredOut = Math.max(0, totalFound - matched);
          renderCounts(matched, filteredOut, totalFound);

          if (!results || !results.length) {
            var msg = config.emptyMessage;
            if (term) msg += ' "' + term + '"';
            showFeedback(msg, "empty");
            return [];
          }
          hideFeedback();
          var totalCount = results.length;
          var pageSize = Math.max(1, config.pageSize);
          var totalPages = Math.ceil(totalCount / pageSize);
          if (currentPage > totalPages) currentPage = totalPages;
          var startIndex = (currentPage - 1) * pageSize;
          var endIndex = startIndex + pageSize;
          var pageItems = results.slice(startIndex, endIndex);
          renderCards(pageItems);
          renderPagination(
            totalCount,
            pageSize,
            currentPage,
            function (targetPage) {
              updateQueryString({ q: term, page: targetPage });
              runSearch(term, targetPage);
            }
          );
        })
        .catch(function () {
          showFeedback(config.errorMessage, "error");
        });
    }
  });
})();
